import * as Sentry from '@sentry/react';

const TUPLES = [
  ['vto.handoff', 'initial', 'changed', 'context_changed'],
  ['vto.handoff', 'initial', 'read_failed', 'unavailable'],
  ['vto.resume', 'completed_resume', 'changed', 'context_changed'],
  ['vto.resume', 'completed_resume', 'read_failed', 'unavailable'],
  ['vto.storage_fallback', 'initial_handoff', 'used', 'storage_unavailable'],
  ['vto.storage_fallback', 'ordinary_selection', 'used', 'storage_unavailable'],
] as const;
type Observation<T> = T extends readonly [infer E, infer P, infer O, infer R]
  ? { event: E; phase: P; outcome: O; reason: R }
  : never;
export type VerifiedTerminalObservation = Observation<(typeof TUPLES)[number]>;
type SentryEnvelope = Parameters<
  NonNullable<ReturnType<typeof Sentry.getClient>>['sendEnvelope']
>[0];
type SentryLog = Parameters<NonNullable<Parameters<typeof Sentry.init>[0]['beforeSendLog']>>[0];
let armedClient: ReturnType<typeof Sentry.getClient>;

function parse(value: unknown): VerifiedTerminalObservation | undefined {
  try {
    if (!value || typeof value !== 'object') return;
    const { event, phase, outcome, reason } = value as Record<string, unknown>;
    if (TUPLES.some(([e, p, o, r]) => event === e && phase === p && outcome === o && reason === r))
      return { event, phase, outcome, reason } as VerifiedTerminalObservation;
  } catch {
    /* fail closed */
  }
  return undefined;
}
export function resetVerifiedTerminalObservations(): void {
  armedClient = undefined;
}
export function verifiedTerminalBeforeSendLog(log: SentryLog): SentryLog | null {
  const observation = parse(log.attributes);
  return observation && log.message === observation.event ? log : null;
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function validId(value: unknown, length: number): value is string {
  return (
    typeof value === 'string' &&
    value.length === length &&
    /^[0-9a-f]+$/.test(value) &&
    !/^0+$/.test(value)
  );
}
function safeHeader(value: unknown): boolean {
  if (!record(value) || Object.keys(value).length !== 1 || !record(value['sdk'])) return false;
  return (
    Object.keys(value['sdk']).length === 2 &&
    value['sdk']['name'] === 'sentry.javascript.react' &&
    value['sdk']['version'] === '9.47.1'
  );
}
function reserved(row: Record<string, unknown>): boolean {
  const attr = record(row['attributes']) && row['attributes']['event'];
  return (
    (typeof row['body'] === 'string' && row['body'].startsWith('vto.')) ||
    (record(attr) && typeof attr['value'] === 'string' && attr['value']['startsWith']('vto.'))
  );
}
function project(row: Record<string, unknown>): Record<string, unknown> | undefined {
  if (
    !record(row['attributes']) ||
    typeof row['timestamp'] !== 'number' ||
    !Number.isFinite(row['timestamp']) ||
    row['level'] !== 'info' ||
    row['severity_number'] !== 9
  )
    return;
  const fields: Record<string, unknown> = {};
  for (const key of ['event', 'phase', 'outcome', 'reason']) {
    const attr = row['attributes'][key];
    if (!record(attr) || attr['type'] !== 'string') return;
    fields[key] = attr['value'];
  }
  const observation = parse(fields);
  if (!observation || row['body'] !== observation.event) return;
  const attributes: Record<string, unknown> = Object.fromEntries(
    Object.entries(observation).map(([key, value]) => [key, { type: 'string', value }]),
  );
  const traceId = row['trace_id'];
  const parent = row['attributes']['sentry.trace.parent_span_id'];
  if (
    validId(traceId, 32) &&
    record(parent) &&
    parent['type'] === 'string' &&
    validId(parent['value'], 16)
  )
    attributes['sentry.trace.parent_span_id'] = { type: 'string', value: parent['value'] };
  return {
    timestamp: row['timestamp'],
    level: 'info',
    severity_number: 9,
    body: observation.event,
    attributes,
    ...(validId(traceId, 32) ? { trace_id: traceId } : {}),
  };
}

/** Public synchronous boundary; unsafe shared headers cause explicitly lossy feature omission. */
function projectEnvelope(envelope: SentryEnvelope): void {
  const safe = safeHeader(envelope[0]);
  for (let index = envelope[1].length - 1; index >= 0; index--) {
    const item = envelope[1][index]!;
    const header = item[0];
    const payload: unknown = item[1];
    if (header.type !== 'log' || !record(payload) || !Array.isArray(payload['items'])) continue;
    const nativeContainer = Object.keys(payload).every((key) => key === 'items');
    const nativeHeader =
      Object.keys(header).every((key) => ['type', 'item_count', 'content_type'].includes(key)) &&
      (header.content_type === undefined ||
        header.content_type === 'application/vnd.sentry.items.log+json');
    let changed = false;
    const rows = payload['items'].flatMap((row: unknown) => {
      if (!record(row) || !reserved(row)) return [row];
      changed = true;
      const projected = safe && nativeContainer && nativeHeader ? project(row) : undefined;
      return projected ? [projected] : [];
    });
    if (!changed) continue;
    if (rows.length === 0) envelope[1].splice(index, 1);
    else {
      // v9 has no version/ingest_settings fields. Preserve its native container.
      item[1] = (
        nativeContainer ? { items: rows } : { ...payload, items: rows }
      ) as (typeof item)[1];
      header.item_count = rows.length;
    }
  }
}
export function armVerifiedTerminalObservations(): void {
  try {
    const client = Sentry.getClient();
    if (!client || client.getOptions().enableLogs !== true || client.getOptions().enabled === false)
      return;
    client.on('beforeEnvelope', projectEnvelope);
    armedClient = client;
  } catch {
    armedClient = undefined;
  }
}

/** No generic attributes/logger escape hatch; call sites supply only a closed tuple. */
export function observeVerifiedTerminal(value: VerifiedTerminalObservation): void {
  try {
    const client = Sentry.getClient();
    if (
      !armedClient ||
      client !== armedClient ||
      client.getOptions().enableLogs !== true ||
      client.getOptions().enabled === false
    )
      return;
    const observation = parse(value);
    if (observation) Sentry.logger.info(observation.event, observation);
  } catch {
    /* never block a handoff or add a business retry */
  }
}
